import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_URL } from '../config';
import { 
  readImportFile, 
  transformImportData, 
  validateImportData,
  detectFileType 
} from '../utils/import';

// Funkce pro detekci sloupce s obchodním zástupcem (OZ)
const detectOzColumn = (data) => {
  if (!data || data.length === 0) return null;
  
  const columns = Object.keys(data[0]);
  const ozColumnNames = ['oz', 'obchodní zástupce', 'obchodni zastupce', 'operator', 'sales rep', 'salesperson', 'rep'];
  
  // Najdi přesnou shodu (case insensitive)
  for (const column of columns) {
    if (ozColumnNames.includes(column.toLowerCase())) {
      return column;
    }
  }
  
  // Najdi částečnou shodu
  for (const column of columns) {
    const lowerColumn = column.toLowerCase();
    if (lowerColumn.includes('oz') || 
        lowerColumn.includes('obchod') || 
        lowerColumn.includes('zastup') || 
        lowerColumn.includes('operator') ||
        lowerColumn.includes('sales') ||
        lowerColumn.includes('rep')) {
      return column;
    }
  }
  
  return null;
};

const ImportWizard = ({ onImportComplete, onClose, updateMode = false, userId, isDarkMode = false }) => {
  const [step, setStep] = useState(1); // 1: výběr souboru, 2: náhled, 3: mapování, 4: detekce duplicit, 5: import
  const [file, setFile] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [transformedData, setTransformedData] = useState([]);
  const [validation, setValidation] = useState({ errors: [], warnings: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [availableColumns, setAvailableColumns] = useState([]);
  const [importProgress, setImportProgress] = useState(0);
  const [updateStats, setUpdateStats] = useState({ updated: 0, created: 0, errors: 0 });
  const [statusFilter, setStatusFilter] = useState([]); // Nový stav pro filtrování podle statusů
  const [uniqueStatuses, setUniqueStatuses] = useState([]); // Unikátní stavy v datech
  const [ozFilter, setOzFilter] = useState([]); // Filtr pro obchodní zástupce
  const [uniqueOzValues, setUniqueOzValues] = useState([]); // Unikátní hodnoty OZ
  const [ozColumnName, setOzColumnName] = useState(''); // Název sloupce pro OZ
  const [savedMappings, setSavedMappings] = useState([]); // Uložená mapování
  const [mappingName, setMappingName] = useState(''); // Název pro uložení mapování
  const [showSaveMappingDialog, setShowSaveMappingDialog] = useState(false); // Dialog pro uložení
  const [batchName, setBatchName] = useState(''); // Název várky pro kontakty
  
  // State pro detekci duplicit
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateDecisions, setDuplicateDecisions] = useState({}); // per-ICO: 'replace', 'skip', 'create_new'
  const [ownerActions, setOwnerActions] = useState({}); // per-owner vizuální stav (oddělen od per-ICO)
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [dupCheckProgress, setDupCheckProgress] = useState({ current: 0, total: 0 });

  // Skupiny duplicit podle vlastníka — stabilní reference, aktualizuje se jen když se změní `duplicates`
  const ownerGroupsMap = useMemo(() => {
    const groups = {};
    duplicates.forEach(dup => {
      const owner = dup.existingCompany?.owner || 'Neznámý';
      if (!groups[owner]) groups[owner] = [];
      groups[owner].push(dup.ico);
    });
    return groups;
  }, [duplicates]);

  const ownerGroupsList = useMemo(() => Object.keys(ownerGroupsMap), [ownerGroupsMap]);

  // Stabilní handler — přijímá jméno vlastníka a akci, IČO vždy načte z aktuální mapy
  const handleOwnerAction = useCallback((ownerName, action) => {
    const icos = ownerGroupsMap[ownerName];
    if (!icos || icos.length === 0) return;
    const updates = {};
    icos.forEach(ico => { updates[ico] = action; });
    setDuplicateDecisions(prev => ({ ...prev, ...updates }));
    setOwnerActions(prev => ({ ...prev, [ownerName]: action })); // Explicitní vizuální stav
  }, [ownerGroupsMap]);

  // Vyčištění starých localStorage dat při načtení komponenty
  useEffect(() => {
    localStorage.removeItem('crm_column_mappings');
  }, []);

  // Pomocná funkce pro počítání filtrovaných firem
  const getFilteredCompaniesCount = () => {
    if (!transformedData.length) return 0;
    
    return transformedData.filter(item => {
      // Filtr podle stavu
      if (statusFilter.length > 0 && statusFilter.length < uniqueStatuses.length) {
        if (item.status && item.status.trim() !== '' && !statusFilter.includes(item.status)) {
          return false;
        }
      }
      
      // Filtr podle OZ (pouze pokud je nastaven částečný filtr)
      if (ozColumnName && ozFilter.length > 0 && ozFilter.length < uniqueOzValues.length) {
        // Použij _ozValue
        if (item._ozValue) {
          if (!ozFilter.includes(item._ozValue)) {
            return false;
          }
        }
      }
      
      return true;
    }).length;
  };

  // Pomocná funkce pro počítání firem podle konkrétního stavu (s ohledem na OZ filtr)
  const getCompaniesCountByStatus = (status) => {
    if (!transformedData.length) return 0;
    
    return transformedData.filter(item => {
      // Musí mít konkrétní stav
      if (item.status !== status) return false;
      
      // Filtr podle OZ (pouze pokud je nastaven částečný filtr)
      if (ozColumnName && ozFilter.length > 0 && ozFilter.length < uniqueOzValues.length) {
        // Použij _ozValue
        if (item._ozValue) {
          if (!ozFilter.includes(item._ozValue)) {
            return false;
          }
        }
      }
      
      return true;
    }).length;
  };

  // Pomocná funkce pro počítání firem podle konkrétního OZ (s ohledem na stav filtr)
  const getCompaniesCountByOz = (ozValue) => {
    if (!transformedData.length) return 0;
    
    return transformedData.filter(item => {
      // Musí mít konkrétní OZ
      if (!item._ozValue || item._ozValue !== ozValue) {
        return false;
      }
      
      // Filtr podle stavu (pouze pokud je nastaven částečný filtr)
      if (statusFilter.length > 0 && statusFilter.length < uniqueStatuses.length) {
        if (item.status && item.status.trim() !== '' && !statusFilter.includes(item.status)) {
          return false;
        }
      }
      
      return true;
    }).length;
  };

  // Načtení uživatelů
  useEffect(() => {
    fetch(`${API_URL}/users?userId=${userId || ''}`)
      .then(res => res.json())
      .then(data => {
        console.log('Načtení uživatelé:', data);
        setUsers(data);
      })
      .catch(err => console.error('Chyba při načítání uživatelů:', err));
  }, []);

  // Načtení uložených mapování z backendu
  useEffect(() => {
    const loadSavedMappings = async () => {
      try {
        console.log('🔍 Loading field mappings for userId:', userId);
        console.log('🌐 API URL:', `${API_URL}/field-mappings?userId=${userId}`);
        
        const response = await fetch(`${API_URL}/field-mappings?userId=${userId}`);
        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);
        
        if (response.ok) {
          const mappings = await response.json();
          console.log('✅ Načteno mapování z backendu:', mappings.length);
          console.log('📋 Mapování data:', mappings);
          setSavedMappings(mappings);
        } else {
          console.error('❌ Chyba při načítání mapování:', response.status);
          const errorText = await response.text();
          console.error('❌ Error response text:', errorText);
          setSavedMappings([]);
        }
      } catch (error) {
        console.error('💥 Chyba při načítání mapování z backendu:', error);
        setSavedMappings([]);
      }
    };

    if (userId) {
      loadSavedMappings();
    }
  }, [userId]);

  // DEBUG funkce pro ověření všech mapování v databázi
  const debugAllMappings = async () => {
    try {
      console.log('🐛 DEBUG: Načítám všechna mapování z databáze...');
      const response = await fetch(`${API_URL}/field-mappings/debug/all`);
      if (response.ok) {
        const debugData = await response.json();
        console.log('🐛 DEBUG: Všechna mapování v databázi:', debugData);
        alert(`Debug: Celkem ${debugData.total} mapování v databázi. Zkontroluj konzoli pro detaily.`);
      } else {
        console.error('❌ DEBUG: Chyba při načítání debug dat:', response.status);
      }
    } catch (error) {
      console.error('💥 DEBUG: Chyba při načítání debug dat:', error);
    }
  };

  // Funkce pro ukládání mapování do backendu
  const saveMappingToBackend = async (name, mapping) => {
    try {
      const response = await fetch(`${API_URL}/field-mappings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name,
          userId: userId,
          mapping: mapping,
          isGlobal: false
        })
      });

      if (response.ok) {
        const newMapping = await response.json();
        setSavedMappings(prev => [...prev, newMapping]);
        console.log('Mapování uloženo do backendu:', newMapping);
        return newMapping;
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Chyba při ukládání mapování');
      }
    } catch (error) {
      console.error('Chyba při ukládání mapování do backendu:', error);
      throw error;
    }
  };

  // Funkce pro načtení mapování
  const loadMappingFromStorage = (mappingId) => {
    const mapping = savedMappings.find(m => m._id === mappingId || m.id === mappingId);
    if (mapping) {
      setColumnMapping(mapping.mapping);
    }
  };

  // Funkce pro smazání mapování z backendu
  const deleteMappingFromBackend = async (mappingId) => {
    try {
      const response = await fetch(`${API_URL}/field-mappings/${mappingId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSavedMappings(prev => prev.filter(m => m._id !== mappingId && m.id !== mappingId));
        console.log('Mapování smazáno z backendu:', mappingId);
      } else {
        throw new Error('Chyba při mazání mapování z backendu');
      }
    } catch (error) {
      console.error('Chyba při mazání mapování z backendu:', error);
      throw error;
    }
  };

  // Funkce pro uložení aktuálního mapování
  const handleSaveMapping = async () => {
    if (!mappingName.trim()) {
      alert('Zadejte název pro mapování.');
      return;
    }
    
    // Kontrola, zda mapování není prázdné
    const hasMapping = Object.values(columnMapping).some(value => value);
    if (!hasMapping) {
      alert('Nemůžete uložit prázdné mapování.');
      return;
    }

    try {
      await saveMappingToBackend(mappingName.trim(), columnMapping);
      setMappingName('');
      setShowSaveMappingDialog(false);
      alert('Mapování bylo úspěšně uloženo.');
    } catch (error) {
      alert('Chyba při ukládání mapování: ' + error.message);
    }
  };

  // Funkce pro export mapování do souboru
  const exportMapping = (mapping) => {
    const dataStr = JSON.stringify(mapping, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `mapovani_${mapping.name}_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Funkce pro import mapování ze souboru
  const importMapping = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedMapping = JSON.parse(e.target.result);
        
        // Validace struktury mapování
        if (!importedMapping.name || !importedMapping.mapping) {
          alert('Neplatný formát souboru mapování.');
          return;
        }

        // Přidej importované mapování do seznamu
        const newMapping = {
          ...importedMapping,
          id: Date.now(),
          createdAt: new Date().toISOString(),
          imported: true
        };

        setSavedMappings(prev => [...prev, newMapping]);
        alert(`Mapování "${importedMapping.name}" bylo úspěšně importováno.`);
      } catch (error) {
        alert('Chyba při načítání souboru mapování. Zkontrolujte formát souboru.');
      }
    };
    reader.readAsText(selectedFile);
    
    // Reset input pro možnost načtení stejného souboru znovu
    event.target.value = '';
  };

  // Funkce pro duplikování mapování
  const duplicateMapping = (mapping) => {
    const duplicatedMapping = {
      id: Date.now(),
      name: `${mapping.name} (kopie)`,
      mapping: { ...mapping.mapping },
      createdAt: new Date().toISOString(),
      duplicated: true
    };

    setSavedMappings(prev => [...prev, duplicatedMapping]);
    alert(`Mapování bylo zduplikováno jako "${duplicatedMapping.name}".`);
  };

  // Standardní CRM sloupce
  const crmColumns = [
    { key: 'ico', label: 'IČO' },
    { key: 'name', label: 'Název firmy' },
    { key: 'address', label: 'Adresa' },
    { key: 'kraj', label: 'Kraj' },
    { key: 'operator', label: 'Operátor' },
    { key: 'status', label: 'Stav' },
    { key: 'forecast', label: 'Forecast' },
    { key: 'bmsl', label: 'BMSL' },
    { key: 'rsEndDate', label: 'Konec RS' },
    { key: 'doNotConnect', label: 'Nepropojovat' },
    { key: 'contactName', label: 'Kontakt - Jméno (1 nebo více oddělených ;)' },
    { key: 'contactPhone', label: 'Kontakt - Telefon (1 nebo více oddělených ;)' },
    { key: 'contactEmail', label: 'Kontakt - Email (1 nebo více oddělených ;)' },
    { key: 'contact1Name', label: 'Kontakt 1 - Jméno' },
    { key: 'contact1Phone', label: 'Kontakt 1 - Telefon' },
    { key: 'contact1Email', label: 'Kontakt 1 - Email' },
    { key: 'contact2Name', label: 'Kontakt 2 - Jméno' },
    { key: 'contact2Phone', label: 'Kontakt 2 - Telefon' },
    { key: 'contact2Email', label: 'Kontakt 2 - Email' },
    { key: 'contact3Name', label: 'Kontakt 3 - Jméno' },
    { key: 'contact3Phone', label: 'Kontakt 3 - Telefon' },
    { key: 'contact3Email', label: 'Kontakt 3 - Email' },
    { key: 'contact4Name', label: 'Kontakt 4 - Jméno' },
    { key: 'contact4Phone', label: 'Kontakt 4 - Telefon' },
    { key: 'contact4Email', label: 'Kontakt 4 - Email' },
    { key: 'activityText', label: 'Aktivita' },
    { key: 'nextActionDate', label: 'Datum další akce' }
  ];

  // Krok 1: Výběr souboru
  const handleFileSelect = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setIsLoading(true);

    try {
      const fileType = detectFileType(selectedFile);
      console.log('Typ souboru:', fileType);

      const data = await readImportFile(selectedFile);
      console.log('Načtená data:', data);

      setRawData(data);
      
      // Získej dostupné sloupce
      if (data.length > 0) {
        setAvailableColumns(Object.keys(data[0]));
        
        // Automatické mapování sloupců
        const autoMapping = {};
        Object.keys(data[0]).forEach(column => {
          const lowerColumn = column.toLowerCase();
          
          // Automatické mapování běžných názvů - přesnější
          if (lowerColumn === 'ico' || lowerColumn === 'ičo') {
            autoMapping[column] = 'ico';
          } else if (lowerColumn === 'name' || lowerColumn === 'název' || lowerColumn === 'nazev') {
            autoMapping[column] = 'name';
          } else if (lowerColumn === 'address' || lowerColumn === 'adresa') {
            autoMapping[column] = 'address';
          } else if (lowerColumn === 'operator') {
            autoMapping[column] = 'operator';
          } else if (lowerColumn === 'status' || lowerColumn === 'stav') {
            autoMapping[column] = 'status';
          } else if (lowerColumn === 'contactname' || lowerColumn.includes('jméno kontaktu')) {
            autoMapping[column] = 'contactName';
          } else if (lowerColumn === 'contactphone' || lowerColumn.includes('telefon kontaktu')) {
            autoMapping[column] = 'contactPhone';
          } else if (lowerColumn === 'contactemail' || lowerColumn.includes('email kontaktu')) {
            autoMapping[column] = 'contactEmail';
          } else if (lowerColumn === 'activitytext' || lowerColumn.includes('text aktivity')) {
            autoMapping[column] = 'activityText';
          } else if (lowerColumn === 'nextactiondate' || lowerColumn.includes('nextaction') || lowerColumn.includes('dalsi') || lowerColumn.includes('další')) {
            autoMapping[column] = 'nextActionDate';
          } else if (lowerColumn === 'forecast') {
            autoMapping[column] = 'forecast';
          } else if (lowerColumn === 'bmsl') {
            autoMapping[column] = 'bmsl';
          } else if (lowerColumn === 'rsenddate' || lowerColumn.includes('konec rs') || lowerColumn.includes('rs konec')) {
            autoMapping[column] = 'rsEndDate';
          } else if (lowerColumn === 'donotconnect' || lowerColumn === 'nepropojovat' || lowerColumn === 'nespamovat') {
            autoMapping[column] = 'doNotConnect';
          } else if (lowerColumn === 'kraj' || lowerColumn === 'region') {
            autoMapping[column] = 'kraj';
          } else if (/^kontakt[_ ]?1[_ -]+jm[ée]no$|^contact[_ ]?1[_ -]+name$|^contact1name$/i.test(lowerColumn)) {
            autoMapping[column] = 'contact1Name';
          } else if (/^kontakt[_ ]?1[_ -]+telefon$|^contact[_ ]?1[_ -]+phone$|^contact1phone$/i.test(lowerColumn)) {
            autoMapping[column] = 'contact1Phone';
          } else if (/^kontakt[_ ]?1[_ -]+email$|^contact[_ ]?1[_ -]+email$|^contact1email$/i.test(lowerColumn)) {
            autoMapping[column] = 'contact1Email';
          } else if (/^kontakt[_ ]?2[_ -]+jm[ée]no$|^contact[_ ]?2[_ -]+name$|^contact2name$/i.test(lowerColumn)) {
            autoMapping[column] = 'contact2Name';
          } else if (/^kontakt[_ ]?2[_ -]+telefon$|^contact[_ ]?2[_ -]+phone$|^contact2phone$/i.test(lowerColumn)) {
            autoMapping[column] = 'contact2Phone';
          } else if (/^kontakt[_ ]?2[_ -]+email$|^contact[_ ]?2[_ -]+email$|^contact2email$/i.test(lowerColumn)) {
            autoMapping[column] = 'contact2Email';
          } else if (/^kontakt[_ ]?3[_ -]+jm[ée]no$|^contact[_ ]?3[_ -]+name$|^contact3name$/i.test(lowerColumn)) {
            autoMapping[column] = 'contact3Name';
          } else if (/^kontakt[_ ]?3[_ -]+telefon$|^contact[_ ]?3[_ -]+phone$|^contact3phone$/i.test(lowerColumn)) {
            autoMapping[column] = 'contact3Phone';
          } else if (/^kontakt[_ ]?3[_ -]+email$|^contact[_ ]?3[_ -]+email$|^contact3email$/i.test(lowerColumn)) {
            autoMapping[column] = 'contact3Email';
          } else if (/^kontakt[_ ]?4[_ -]+jm[ée]no$|^contact[_ ]?4[_ -]+name$|^contact4name$/i.test(lowerColumn)) {
            autoMapping[column] = 'contact4Name';
          } else if (/^kontakt[_ ]?4[_ -]+telefon$|^contact[_ ]?4[_ -]+phone$|^contact4phone$/i.test(lowerColumn)) {
            autoMapping[column] = 'contact4Phone';
          } else if (/^kontakt[_ ]?4[_ -]+email$|^contact[_ ]?4[_ -]+email$|^contact4email$/i.test(lowerColumn)) {
            autoMapping[column] = 'contact4Email';
          }
        });
        
        setColumnMapping(autoMapping);
      }

      setStep(2);
    } catch (error) {
      console.error('Chyba při načítání souboru:', error);
      alert(`Chyba při načítání souboru: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Krok 2: Náhled a transformace dat
  const handlePreview = () => {
    if (rawData.length === 0) return;

    setIsLoading(true);
    try {
      // Aplikuj mapování sloupců na surová data
      const mappedData = rawData.map((row, index) => {
        const mappedRow = {};
        
        // Použij mapování sloupců
        Object.entries(columnMapping).forEach(([sourceColumn, targetColumn]) => {
          if (targetColumn && row[sourceColumn] !== undefined) {
            mappedRow[targetColumn] = row[sourceColumn];
          }
        });
        
        // Přidej původní data pro pozdější použití v filtrech
        mappedRow._originalData = row;
        mappedRow._originalIndex = index;
        
        return mappedRow;
      });

      // Detekuj sloupec OZ v rawData před transformací
      const ozColumn = detectOzColumn(rawData);
      if (ozColumn) {
        // Přidej OZ hodnoty do mappedData
        mappedData.forEach((mappedRow, index) => {
          if (rawData[index] && rawData[index][ozColumn]) {
            mappedRow._ozValue = rawData[index][ozColumn];
          }
        });
      }

      const transformed = transformImportData(mappedData, columnMapping);
      
      // Debug log pro kontrolu transformovaných dat
      console.log('KONTROLA TRANSFORMOVANÝCH DAT:');
      console.log('První transformovaná firma:', JSON.stringify(transformed[0], null, 2));
      console.log('Mapování sloupců:', columnMapping);
      
      // Nastav OZ informace
      if (ozColumn) {
        setOzColumnName(ozColumn);
        
        const ozValues = [...new Set(
          rawData
            .map(item => item[ozColumn])
            .filter(value => value && value.toString().trim() !== '')
        )].sort();
        
        setUniqueOzValues(ozValues);
        setOzFilter(ozValues); // Defaultně vyber všechny hodnoty
      } else {
        setOzColumnName('');
        setUniqueOzValues([]);
        setOzFilter([]);
      }
      
      const validationResults = validateImportData(transformed);
      
      // Extrahuj unikátní stavy pro filtrování
      const statuses = [...new Set(
        transformed
          .map(item => item.status)
          .filter(status => status && status.trim() !== '')
      )].sort();
      
      setUniqueStatuses(statuses);
      setStatusFilter(statuses); // Defaultně vyber všechny stavy
      
      setTransformedData(transformed);
      setValidation(validationResults);
      setStep(3);
    } catch (error) {
      console.error('Chyba při transformaci dat:', error);
      alert(`Chyba při zpracování dat: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Krok 4: Kontrola duplicit
  const handleCheckDuplicates = async () => {
    setIsCheckingDuplicates(true);
    setDupCheckProgress({ current: 0, total: 0 });
    
    try {
      // Filtruj data podle vybraných stavů a OZ
      const filteredData = transformedData.filter((item) => {
        if (statusFilter.length > 0 && statusFilter.length < uniqueStatuses.length) {
          if (item.status && item.status.trim() !== '' && !statusFilter.includes(item.status)) return false;
        }
        if (ozColumnName && ozFilter.length > 0 && ozFilter.length < uniqueOzValues.length) {
          if (item._ozValue && !ozFilter.includes(item._ozValue)) return false;
        }
        return true;
      });

      const icosToCheck = filteredData
        .filter(item => item.ico && item.ico.trim() !== '')
        .map(item => item.ico.trim());

      if (icosToCheck.length === 0) {
        setStep(5);
        return;
      }

      // Kontrola po chuncích 2 000 IČO — zabrání problémům s velkými těly requestů a MongoDB $in
      const CHUNK_SIZE = 2000;
      const totalChunks = Math.ceil(icosToCheck.length / CHUNK_SIZE);
      let allDuplicates = [];

      setDupCheckProgress({ current: 0, total: totalChunks });

      for (let i = 0; i < icosToCheck.length; i += CHUNK_SIZE) {
        const chunk = icosToCheck.slice(i, i + CHUNK_SIZE);
        try {
          const response = await fetch(`${API_URL}/check-duplicates`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ icos: chunk, userId })
          });
          if (response.ok) {
            const data = await response.json();
            allDuplicates = allDuplicates.concat(data.duplicates || []);
          }
        } catch (err) {
          console.warn('Chyba při kontrole chunku duplicit:', err);
        }
        setDupCheckProgress({ current: Math.floor(i / CHUNK_SIZE) + 1, total: totalChunks });
        // Pauza mezi chuncemi — vždy, aby měl React čas překreslit progress
        await new Promise(r => setTimeout(r, 80));
      }

      // Dej Reactu čas překreslit progress na 100 % před těžkou operací
      await new Promise(r => setTimeout(r, 50));

      if (allDuplicates.length > 0) {
        // O(1) lookup místo O(n²) filteredData.find()
        const filteredDataByIco = new Map(
          filteredData
            .filter(item => item.ico && item.ico.trim() !== '')
            .map(item => [item.ico.trim(), item])
        );

        const duplicatesWithImportData = allDuplicates.map(dup => ({
          ...dup,
          importData: filteredDataByIco.get(dup.ico)
        }));

        // Výchozí rozhodnutí = přeskočit (jedno hromadné setState)
        const initialDecisions = {};
        duplicatesWithImportData.forEach(dup => { initialDecisions[dup.ico] = 'skip'; });

        // Výchozí vizuální stav vlastníků = přeskočit
        const ownerGroupsInline = {};
        duplicatesWithImportData.forEach(dup => {
          const owner = dup.existingCompany?.owner || 'Neznámý';
          if (!ownerGroupsInline[owner]) ownerGroupsInline[owner] = true;
        });
        const initialOwnerActions = {};
        Object.keys(ownerGroupsInline).forEach(owner => { initialOwnerActions[owner] = 'skip'; });

        setDuplicates(duplicatesWithImportData);
        setDuplicateDecisions(initialDecisions);
        setOwnerActions(initialOwnerActions);
        setStep(4);
      } else {
        setStep(5);
      }
    } catch (error) {
      console.error('Chyba při kontrole duplicit:', error);
      setStep(5); // Při chybě pokračuj bez kontroly
    } finally {
      setIsCheckingDuplicates(false);
      setDupCheckProgress({ current: 0, total: 0 });
    }
  };

  // Krok 5: Finální import
  const handleImport = async () => {
    if (!selectedUserId) {
      alert('Vyberte uživatele pro přiřazení firem.');
      return;
    }

    if (validation.errors.length > 0) {
      if (!window.confirm('Existují chyby v datech. Chcete pokračovat s importem?')) {
        return;
      }
    }

    // Filtruj data podle vybraných stavů a OZ
    const filteredData = transformedData.filter((item, index) => {
      // Filtr podle stavu (pouze pokud je nastaven částečný filtr)
      if (statusFilter.length > 0 && statusFilter.length < uniqueStatuses.length) {
        if (item.status && item.status.trim() !== '' && !statusFilter.includes(item.status)) {
          return false;
        }
      }
      
      // Filtr podle OZ (pouze pokud je nastaven částečný filtr)
      if (ozColumnName && ozFilter.length > 0 && ozFilter.length < uniqueOzValues.length) {
        // Použij _ozValue
        if (item._ozValue) {
          if (!ozFilter.includes(item._ozValue)) {
            return false;
          }
        }
      }
      
      return true;
    });

    // Filtruj data podle rozhodnutí o duplicitách
    const finalData = filteredData.filter(item => {
      if (item.ico && duplicateDecisions[item.ico.trim()]) {
        return duplicateDecisions[item.ico.trim()] !== 'skip';
      }
      return true;
    });

    if (finalData.length === 0) {
      alert('Žádné záznamy nebudou importovány (všechny přeskočeny).');
      return;
    }

    // Označ záznamy, které mají být nahrazeny nebo sloučeny
    const finalDataWithActions = finalData.map(item => {
      if (item.ico && duplicateDecisions[item.ico.trim()] === 'replace') {
        return { ...item, _replaceExisting: true };
      }
      if (item.ico && duplicateDecisions[item.ico.trim()] === 'merge') {
        return { ...item, _mergeExisting: true };
      }
      return item;
    });

    if (finalData.length < transformedData.length) {
      const excludedCount = transformedData.length - finalData.length;
      if (!window.confirm(`Bude importováno ${finalData.length} záznamů z ${transformedData.length}. ${excludedCount} záznamů bude přeskočeno kvůli filtrům nebo rozhodnutím o duplicitách. Pokračovat?`)) {
        return;
      }
    }

    setIsLoading(true);
    setImportProgress(0);
    setUpdateStats({ updated: 0, created: 0, errors: 0 });
    
    try {
      if (updateMode) {
        await handleUpdateImport(finalDataWithActions);
      } else {
        await handleStandardImport(finalDataWithActions);
      }
    } catch (error) {
      console.error('Chyba při importu:', error);
      
      // Lepší error handling
      let errorMessage = 'Neznámá chyba při importu';
      if (error.message.includes('PayloadTooLargeError')) {
        errorMessage = 'Soubor je příliš velký. Zkuste importovat menší soubor nebo rozdělte data na více části.';
      } else if (error.message.includes('network')) {
        errorMessage = 'Chyba sítě. Zkontrolujte připojení k internetu.';
      } else if (error.message.includes('timeout')) {
        errorMessage = 'Import trval příliš dlouho. Zkuste menší soubor.';
      } else {
        errorMessage = error.message;
      }

      alert(`Chyba při importu: ${errorMessage}`);
    } finally {
      setIsLoading(false);
      setImportProgress(0);
    }
  };

  // Standardní import
  const handleStandardImport = async (dataToImport = transformedData) => {
    // Pro velké soubory rozdělíme import na menší části
    const CHUNK_SIZE = 500;
    let totalImported = 0;
    const totalChunks = Math.ceil(dataToImport.length / CHUNK_SIZE);
    
    for (let i = 0; i < dataToImport.length; i += CHUNK_SIZE) {
      const chunk = dataToImport.slice(i, i + CHUNK_SIZE);
      const chunkNumber = Math.floor(i / CHUNK_SIZE) + 1;
      
      // Vyčisti _originalData a _ozValue před odesláním (zachovej _replaceExisting)
      const cleanedChunk = chunk.map(item => {
        const cleanedItem = { ...item };
        delete cleanedItem._originalData;
        delete cleanedItem._ozValue;
        // _replaceExisting zůstane
        return cleanedItem;
      });
      
      // Debug log pro kontrolu dat před odesláním
      if (chunkNumber === 1) {
        console.log('KONTROLA DAT PŘED ODESLÁNÍM NA SERVER:');
        console.log('První firma z chunku:', JSON.stringify(cleanedChunk[0], null, 2));
      }
      
      console.log(`Importuji chunk ${chunkNumber}/${totalChunks}`);
      setImportProgress((chunkNumber / totalChunks) * 100);
      
      const response = await fetch(`${API_URL}/import-companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companies: cleanedChunk,
          userId: selectedUserId,
          batchName: batchName || undefined
        })
      });

      if (response.ok) {
        const result = await response.json();
        totalImported += result.count;
        console.log(`Chunk imported: ${result.count} companies`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
    }

    alert(`Import úspěšně dokončen! Importováno ${totalImported} firem.`);
    onImportComplete?.();
    onClose?.();
  };

  // Update import - aktualizuje existující firmy podle IČO
  const handleUpdateImport = async (dataToImport = transformedData) => {
    let stats = { updated: 0, created: 0, errors: 0 };
    const total = dataToImport.length;
    
    for (let i = 0; i < dataToImport.length; i++) {
      const companyRaw = dataToImport[i];
      // Vyčisti _originalData a _ozValue
      const company = { ...companyRaw };
      delete company._originalData;
      delete company._ozValue;
      
      setImportProgress((i / total) * 100);
      
      try {
        if (company.ico) {
          // Pokus o aktualizaci existující firmy
          const existingResponse = await fetch(`${API_URL}/companies?ico=${company.ico}&userId=${selectedUserId}`);
          const existingCompanies = await existingResponse.json();
          
          if (existingCompanies.length > 0) {
            // Aktualizuj existující firmu
            const existingCompany = existingCompanies[0];
            const updateData = { ...company };
            
            // Merguj kontakty a aktivity
            if (existingCompany.contacts && company.contacts) {
              updateData.contacts = mergeContacts(existingCompany.contacts, company.contacts);
            }
            if (existingCompany.activities && company.activities) {
              updateData.activities = [...existingCompany.activities, ...company.activities];
            }
            
            const updateResponse = await fetch(`${API_URL}/companies/${existingCompany._id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(updateData)
            });
            
            if (updateResponse.ok) {
              stats.updated++;
            } else {
              throw new Error('Chyba při aktualizaci');
            }
          } else {
            // Vytvoř novou firmu
            const createResponse = await fetch(`${API_URL}/companies`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                ...company, 
                userId: selectedUserId,
                batchName: batchName || undefined
              })
            });
            
            if (createResponse.ok) {
              stats.created++;
            } else {
              throw new Error('Chyba při vytváření');
            }
          }
        } else {
          // Bez IČO vytvoř novou firmu
          const createResponse = await fetch(`${API_URL}/companies`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              ...company, 
              userId: selectedUserId,
              batchName: batchName || undefined
            })
          });
          
          if (createResponse.ok) {
            stats.created++;
          } else {
            throw new Error('Chyba při vytváření');
          }
        }
      } catch (error) {
        console.error(`Chyba při zpracování firmy ${company.name}:`, error);
        stats.errors++;
      }
      
      setUpdateStats(stats);
    }
    
    alert(`Aktualizace dokončena!\nAktualizováno: ${stats.updated} firem\nVytvořeno: ${stats.created} firem\nChyby: ${stats.errors}`);
    onImportComplete?.();
    onClose?.();
  };

  // Merguje kontakty - přidá nové, aktualizuje existující podle email/telefonu
  const mergeContacts = (existingContacts, newContacts) => {
    const merged = [...existingContacts];
    
    newContacts.forEach(newContact => {
      const existingIndex = merged.findIndex(existing => 
        existing.email === newContact.email || existing.phone === newContact.phone
      );
      
      if (existingIndex >= 0) {
        // Aktualizuj existující kontakt
        merged[existingIndex] = { ...merged[existingIndex], ...newContact };
      } else {
        // Přidej nový kontakt
        merged.push(newContact);
      }
    });
    
    return merged;
  };

  // Aktualizace mapování sloupce
  const updateColumnMapping = (sourceColumn, targetColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [sourceColumn]: targetColumn
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {updateMode ? 'Aktualizace firem pomocí importu' : 'Import firem'}
            </h2>
            {updateMode && (
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                Aktualizuje existující firmy podle IČO a vytvoří nové firmy bez IČO
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className={`text-2xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
          >
            ×
          </button>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Krok {step} z 5</span>
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {step === 1 && 'Výběr souboru'}
              {step === 2 && 'Náhled dat'}
              {step === 3 && 'Kontrola a filtrování'}
              {step === 4 && 'Detekce duplicit'}
              {step === 5 && 'Import'}
            </span>
          </div>
          <div className={`w-full rounded-full h-2 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 5) * 100}%` }}
            />
          </div>
        </div>

        {/* Krok 1: Výběr souboru */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Vyberte soubor pro import (CSV, Excel)
              </label>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className={`block w-full text-sm
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  ${isDarkMode 
                    ? 'text-gray-300 file:bg-blue-800 file:text-blue-100 hover:file:bg-blue-700' 
                    : 'text-gray-500 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100'
                  }`}
              />
            </div>
            
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>Podporované formáty:</h3>
              <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                <li>• CSV soubory (s oddělovačem ; nebo ,)</li>
                <li>• Excel soubory (.xlsx, .xls)</li>
                <li>• Automatické čištění HTML entit a tagů</li>
                <li>• Podpora více kontaktů v jednom poli</li>
                <li>• Normalizace telefonních čísel a emailů</li>
              </ul>
            </div>

            {isLoading && (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Načítám soubor...</p>
              </div>
            )}
          </div>
        )}

        {/* Krok 2: Náhled dat */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Náhled dat ({rawData.length} řádků)</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setStep(1)}
                  className={`px-3 py-1 text-sm rounded ${
                    isDarkMode 
                      ? 'bg-gray-600 text-white hover:bg-gray-700' 
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  Zpět
                </button>
                <button
                  onClick={handlePreview}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  disabled={isLoading}
                >
                  {isLoading ? 'Zpracovávám...' : 'Pokračovat'}
                </button>
              </div>
            </div>

            {/* Mapování sloupců */}
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <div className="flex justify-between items-center mb-3">
                <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Mapování sloupců</h4>
                <div className="flex gap-2">
                  {/* Načítání uložených mapování */}
                  {savedMappings.length > 0 && (
                    <div className="flex items-center gap-2">
                      <select
                        className={`text-sm border rounded px-2 py-1 ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300 text-gray-800'
                        }`}
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            loadMappingFromStorage(e.target.value);
                          }
                        }}
                      >
                        <option value="">Načíst uložené mapování...</option>
                        {savedMappings.map(mapping => (
                          <option key={mapping._id || mapping.id} value={mapping._id || mapping.id}>
                            {mapping.name} ({new Date(mapping.createdAt).toLocaleDateString()})
                            {mapping.imported && ' [Importováno]'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Import mapování ze souboru */}
                  <label className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 cursor-pointer">
                    Import mapování
                    <input
                      type="file"
                      accept=".json"
                      onChange={importMapping}
                      className="hidden"
                    />
                  </label>
                  
                  {/* Tlačítko pro uložení mapování */}
                  <button
                    onClick={() => setShowSaveMappingDialog(true)}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                    title="Uložit aktuální mapování"
                  >
                    Uložit mapování
                  </button>
                </div>
              </div>

              {/* Dialog pro uložení mapování */}
              {showSaveMappingDialog && (
                <div className={`mb-4 p-3 border rounded ${
                  isDarkMode
                    ? 'bg-blue-900/20 border-blue-600'
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <label className={`text-sm font-medium ${
                      isDarkMode ? 'text-blue-200' : 'text-blue-900'
                    }`}>Název mapování:</label>
                    <input
                      type="text"
                      value={mappingName}
                      onChange={(e) => setMappingName(e.target.value)}
                      placeholder="Zadejte název pro mapování..."
                      className={`flex-1 text-sm border rounded px-2 py-1 ${
                        isDarkMode
                          ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-800'
                      }`}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveMapping();
                        }
                      }}
                    />
                    <button
                      onClick={handleSaveMapping}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Uložit
                    </button>
                    <button
                      onClick={() => {
                        setShowSaveMappingDialog(false);
                        setMappingName('');
                      }}
                      className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      Zrušit
                    </button>
                  </div>
                  <p className={`text-xs ${isDarkMode ? 'text-blue-300' : 'text-gray-600'}`}>
                    Uložené mapování můžete znovu použít při dalších importech. Mapování lze také exportovat a sdílet s kolegy.
                  </p>
                </div>
              )}

              {/* Seznam uložených mapování s možností smazání */}
              {savedMappings.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Uložená mapování:</h5>
                    <button
                      onClick={debugAllMappings}
                      className={`px-2 py-1 text-xs rounded ${
                        isDarkMode
                          ? 'bg-orange-800/30 hover:bg-orange-800/50 text-orange-300'
                          : 'bg-orange-100 hover:bg-orange-200 text-orange-800'
                      }`}
                      title="Debug: Zobrazit všechna mapování v databázi"
                    >
                      🐛 Debug DB
                    </button>
                  </div>
                  <div className="space-y-1">
                    {savedMappings.map(mapping => (
                      <div key={mapping._id || mapping.id} className={`flex items-center justify-between text-sm p-2 rounded border ${
                        isDarkMode
                          ? 'bg-gray-600 border-gray-500 text-white'
                          : 'bg-white border-gray-300 text-gray-800'
                      }`}>
                        <span>
                          <strong>{mapping.name}</strong> 
                          <span className={`ml-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                            ({new Date(mapping.createdAt).toLocaleDateString()})
                          </span>
                          {mapping.imported && (
                            <span className={`ml-2 px-1 py-0.5 text-xs rounded ${
                              isDarkMode
                                ? 'bg-purple-800/30 text-purple-300'
                                : 'bg-purple-100 text-purple-800'
                            }`}>
                              Importováno
                            </span>
                          )}
                          {mapping.duplicated && (
                            <span className={`ml-2 px-1 py-0.5 text-xs rounded ${
                              isDarkMode
                                ? 'bg-blue-800/30 text-blue-300'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              Kopie
                            </span>
                          )}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => loadMappingFromStorage(mapping._id || mapping.id)}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                            title="Načíst toto mapování"
                          >
                            Načíst
                          </button>
                          <button
                            onClick={() => duplicateMapping(mapping)}
                            className="px-2 py-1 text-xs bg-cyan-600 text-white rounded hover:bg-cyan-700"
                            title="Duplikovat mapování"
                          >
                            Duplikovat
                          </button>
                          <button
                            onClick={() => exportMapping(mapping)}
                            className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                            title="Exportovat mapování do souboru"
                          >
                            Export
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Opravdu chcete smazat mapování "${mapping.name}"?`)) {
                                deleteMappingFromBackend(mapping._id || mapping.id);
                              }
                            }}
                            className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                            title="Smazat mapování"
                          >
                            Smazat
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{availableColumns.map(column => (
                  <div key={column} className="flex items-center gap-2">
                    <span className={`text-sm font-medium min-w-32 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{column}:</span>
                    <select
                      value={columnMapping[column] || ''}
                      onChange={(e) => updateColumnMapping(column, e.target.value)}
                      className={`flex-1 text-sm border rounded px-2 py-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      <option value="">-- Nesmapovat --</option>
                      {crmColumns.map(col => (
                        <option key={col.key} value={col.key}>{col.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {/* Náhled prvních 5 řádků */}
            <div className="overflow-x-auto">
              <table className={`min-w-full border-collapse border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                <thead>
                  <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                    {availableColumns.map(column => (
                      <th key={column} className={`border px-2 py-1 text-left text-sm ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rawData.slice(0, 5).map((row, index) => (
                    <tr key={index}>
                      {availableColumns.map(column => (
                        <td key={column} className={`border px-2 py-1 text-sm ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>
                          {String(row[column] || '').substring(0, 50)}
                          {String(row[column] || '').length > 50 && '...'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Krok 3: Kontrola a import */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Kontrola před importem</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setStep(2)}
                  className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                >
                  Zpět
                </button>
              </div>
            </div>

            {/* Výběr uživatele */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Přiřadit firmy uživateli:
              </label>
              <select
                value={selectedUserId}
                onChange={(e) => {
                  console.log('Select changed to:', e.target.value);
                  setSelectedUserId(e.target.value);
                }}
                className="border rounded px-3 py-2 w-full"
                required
              >
                <option value="">-- Vyberte uživatele --</option>
                {users.map(user => {
                  console.log('User mapping:', user._id, user.name, user.email);
                  return (
                    <option key={user._id} value={user._id}>
                      {user.name} ({user.email})
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Název várky pro kontakty */}
            {selectedUserId && users.find(u => u._id === selectedUserId)?.name?.toLowerCase().includes('kontakt') && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Název várky kontaktů (volitelné):
                </label>
                <input
                  type="text"
                  value={batchName}
                  onChange={(e) => setBatchName(e.target.value)}
                  placeholder="např. 'Leadové firmy - leden 2025'"
                  className={`border rounded px-3 py-2 w-full ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Tento název vám pomůže později identifikovat tuto várku kontaktů při distribuci.
                </p>
              </div>
            )}

            {/* Filtrování podle stavů */}
            {uniqueStatuses.length > 0 && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Importovat pouze firmy s těmito stavy:
                </label>
                <div className={`border rounded p-3 max-h-40 overflow-y-auto ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="mb-2">
                    <button
                      type="button"
                      onClick={() => setStatusFilter(uniqueStatuses)}
                      className={`text-xs px-2 py-1 rounded mr-2 ${isDarkMode ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-700'}`}
                    >
                      Vybrat vše
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatusFilter([])}
                      className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}
                    >
                      Zrušit výběr
                    </button>
                  </div>
                  <div className="space-y-1">
                    {uniqueStatuses.map(status => (
                      <label key={status} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={statusFilter.includes(status)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setStatusFilter(prev => [...prev, status]);
                            } else {
                              setStatusFilter(prev => prev.filter(s => s !== status));
                            }
                          }}
                          className="mr-2"
                        />
                        <span className={`flex-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{status}</span>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          ({getCompaniesCountByStatus(status)})
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Vybrané stavy: {statusFilter.length} z {uniqueStatuses.length} | 
                  Po aplikaci filtrů: {getFilteredCompaniesCount()} firem z {transformedData.length}
                </div>
              </div>
            )}

            {/* Filtrování podle OZ */}
            {ozColumnName && uniqueOzValues.length > 0 && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Importovat pouze firmy s těmito obchodními zástupci (sloupec "{ozColumnName}"):
                </label>
                <div className={`border rounded p-3 max-h-40 overflow-y-auto ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="mb-2">
                    <button
                      type="button"
                      onClick={() => setOzFilter(uniqueOzValues)}
                      className={`text-xs px-2 py-1 rounded mr-2 ${isDarkMode ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-700'}`}
                    >
                      Vybrat vše
                    </button>
                    <button
                      type="button"
                      onClick={() => setOzFilter([])}
                      className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}
                    >
                      Zrušit výběr
                    </button>
                  </div>
                  <div className="space-y-1">
                    {uniqueOzValues.map(ozValue => (
                      <label key={ozValue} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={ozFilter.includes(ozValue)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setOzFilter(prev => [...prev, ozValue]);
                            } else {
                              setOzFilter(prev => prev.filter(oz => oz !== ozValue));
                            }
                          }}
                          className="mr-2"
                        />
                        <span className={`flex-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{ozValue}</span>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          ({getCompaniesCountByOz(ozValue)})
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Vybrané OZ: {ozFilter.length} z {uniqueOzValues.length} | 
                  Po aplikaci filtrů: {getFilteredCompaniesCount()} firem z {transformedData.length}
                </div>
              </div>
            )}

            {/* Statistiky */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className={`p-3 rounded ${isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50'}`}>
                <div className={`text-2xl font-bold ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>{transformedData.length}</div>
                <div className={`text-sm ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>Celkem firem</div>
              </div>
              <div className={`p-3 rounded ${isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50'}`}>
                <div className={`text-2xl font-bold ${isDarkMode ? 'text-green-300' : 'text-green-600'}`}>{getFilteredCompaniesCount()}</div>
                <div className={`text-sm ${isDarkMode ? 'text-green-200' : 'text-green-800'}`}>K importu</div>
              </div>
              <div className={`p-3 rounded ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-50'}`}>
                <div className={`text-2xl font-bold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>{validation.warnings.length}</div>
                <div className={`text-sm ${isDarkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>Varování</div>
              </div>
              <div className={`p-3 rounded ${isDarkMode ? 'bg-red-900/30 border border-red-700' : 'bg-red-50'}`}>
                <div className={`text-2xl font-bold ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{validation.errors.length}</div>
                <div className={`text-sm ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>Chyby</div>
              </div>
            </div>

            {/* Chyby a varování */}
            {(validation.errors.length > 0 || validation.warnings.length > 0) && (
              <div className="space-y-2">
                {validation.errors.length > 0 && (
                  <div className={`border rounded p-3 ${isDarkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'}`}>
                    <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>Chyby:</h4>
                    <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                      {validation.errors.slice(0, 10).map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                      {validation.errors.length > 10 && (
                        <li className={isDarkMode ? 'text-red-400' : 'text-red-600'}>... a dalších {validation.errors.length - 10} chyb</li>
                      )}
                    </ul>
                  </div>
                )}

                {validation.warnings.length > 0 && (
                  <div className={`border rounded p-3 ${isDarkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-200'}`}>
                    <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>Varování:</h4>
                    <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                      {validation.warnings.slice(0, 10).map((warning, index) => (
                        <li key={index}>• {warning}</li>
                      ))}
                      {validation.warnings.length > 10 && (
                        <li className={isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}>... a dalších {validation.warnings.length - 10} varování</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Náhled transformovaných dat */}
            <div>
              <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Náhled transformovaných dat (prvních 3 řádků):</h4>
              <div className="overflow-x-auto">
                <table className={`min-w-full border-collapse border text-sm ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <thead>
                    <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                      <th className={`border px-2 py-1 text-left ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>Název</th>
                      <th className={`border px-2 py-1 text-left ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>IČO</th>
                      <th className={`border px-2 py-1 text-left ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>Stav</th>
                      <th className={`border px-2 py-1 text-left ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>Operátor</th>
                      <th className={`border px-2 py-1 text-left ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>BMSL</th>
                      <th className={`border px-2 py-1 text-left ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>Forecast</th>
                      <th className={`border px-2 py-1 text-left ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>Adresa</th>
                      <th className={`border px-2 py-1 text-left ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>Kontakty</th>
                      <th className={`border px-2 py-1 text-left ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>Aktivity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transformedData.slice(0, 3).map((row, index) => (
                      <tr key={index}>
                        <td className={`border px-2 py-1 ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>{row.name || '-'}</td>
                        <td className={`border px-2 py-1 ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>{row.ico || '-'}</td>
                        <td className={`border px-2 py-1 ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>{row.status || '-'}</td>
                        <td className={`border px-2 py-1 ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>{row.operator || '-'}</td>
                        <td className={`border px-2 py-1 ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>{row.bmsl || '-'}</td>
                        <td className={`border px-2 py-1 ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>{row.forecast || '-'}</td>
                        <td className={`border px-2 py-1 ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>{row.address || '-'}</td>
                        <td className={`border px-2 py-1 ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>
                          {row.contacts?.length || 0} kontaktů
                        </td>
                        <td className={`border px-2 py-1 ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`}>
                          {row.activities?.length || 0} aktivit
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tlačítko importu */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              {isLoading && (
                <div className="flex-1 mr-4">
                  <div className={`text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {updateMode ? 'Aktualizuji data...' : 'Importuji data...'} {Math.round(importProgress)}%
                  </div>
                  <div className={`w-full rounded-full h-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div 
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${importProgress}%` }}
                    />
                  </div>
                  
                  {/* Update stats */}
                  {updateMode && (updateStats.updated > 0 || updateStats.created > 0 || updateStats.errors > 0) && (
                    <div className="mt-1 text-xs space-x-3">
                      <span className="text-blue-600">Aktualizováno: {updateStats.updated}</span>
                      <span className="text-green-600">Vytvořeno: {updateStats.created}</span>
                      {updateStats.errors > 0 && <span className="text-red-600">Chyby: {updateStats.errors}</span>}
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={() => setStep(2)}
                disabled={isLoading}
                className={`px-4 py-2 rounded transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-600 text-gray-200 hover:bg-gray-500 disabled:bg-gray-700 disabled:text-gray-500' 
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400 disabled:bg-gray-200'
                }`}
              >
                Zpět
              </button>
              <button
                onClick={handleCheckDuplicates}
                disabled={isLoading || !selectedUserId || isCheckingDuplicates}
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
              >
                {isCheckingDuplicates
                  ? dupCheckProgress.total > 1
                    ? `Kontroluji duplicity… ${dupCheckProgress.current}/${dupCheckProgress.total}`
                    : 'Kontroluji duplicity…'
                  : 'Zkontrolovat duplicity'}
              </button>
            </div>
          </div>
        )}

        {/* Krok 4: Detekce duplicit */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Nalezené duplicity podle IČO
                <span className={`ml-2 text-sm font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  ({duplicates.length} duplicit)
                </span>
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setStep(3)}
                  className={`px-3 py-1 text-sm rounded transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                >
                  Zpět
                </button>
              </div>
            </div>

            <div className={`border rounded-lg p-4 mb-4 ${
              isDarkMode 
                ? 'bg-yellow-900/20 border-yellow-600 text-yellow-100' 
                : 'bg-yellow-50 border-yellow-200 text-yellow-800'
            }`}>
              <p className="text-sm">
                Nalezeny duplicity podle IČO. Zvolte hromadnou akci nebo nastavte každou duplicitu zvlášť.
              </p>
            </div>

            {/* Hromadná akce pro VŠECHNY duplicity (vždy viditelná) */}
            {duplicates.length > 0 && (
              <div className={`border-2 rounded-lg p-4 ${isDarkMode ? 'bg-gray-700 border-blue-600' : 'bg-blue-50 border-blue-300'}`}>
                <p className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-blue-200' : 'text-blue-900'}`}>
                  ⚡ Hromadná akce pro všechny {duplicates.length} duplicit:
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      const all = {};
                      duplicates.forEach(d => { all[d.ico] = 'skip'; });
                      setDuplicateDecisions(all);
                      const allOwner = {};
                      ownerGroupsList.forEach(o => { allOwner[o] = 'skip'; });
                      setOwnerActions(allOwner);
                    }}
                    className={`px-3 py-2 rounded text-sm font-medium transition ${isDarkMode ? 'bg-red-800 hover:bg-red-700 text-white' : 'bg-red-100 hover:bg-red-200 text-red-800 border border-red-300'}`}
                  >⏭️ Přeskočit vše</button>
                  <button
                    onClick={() => {
                      const all = {};
                      duplicates.forEach(d => { all[d.ico] = 'replace'; });
                      setDuplicateDecisions(all);
                      const allOwner = {};
                      ownerGroupsList.forEach(o => { allOwner[o] = 'replace'; });
                      setOwnerActions(allOwner);
                    }}
                    className={`px-3 py-2 rounded text-sm font-medium transition ${isDarkMode ? 'bg-orange-800 hover:bg-orange-700 text-white' : 'bg-orange-100 hover:bg-orange-200 text-orange-800 border border-orange-300'}`}
                  >🔄 Nahradit vše</button>
                  <button
                    onClick={() => {
                      const all = {};
                      duplicates.forEach(d => { all[d.ico] = 'merge'; });
                      setDuplicateDecisions(all);
                      const allOwner = {};
                      ownerGroupsList.forEach(o => { allOwner[o] = 'merge'; });
                      setOwnerActions(allOwner);
                    }}
                    className={`px-3 py-2 rounded text-sm font-medium transition ${isDarkMode ? 'bg-blue-800 hover:bg-blue-700 text-white' : 'bg-blue-100 hover:bg-blue-200 text-blue-800 border border-blue-300'}`}
                  >🔀 Sloučit vše</button>
                  <button
                    onClick={() => {
                      const all = {};
                      duplicates.forEach(d => { all[d.ico] = 'create_new'; });
                      setDuplicateDecisions(all);
                      const allOwner = {};
                      ownerGroupsList.forEach(o => { allOwner[o] = 'create_new'; });
                      setOwnerActions(allOwner);
                    }}
                    className={`px-3 py-2 rounded text-sm font-medium transition ${isDarkMode ? 'bg-green-800 hover:bg-green-700 text-white' : 'bg-green-100 hover:bg-green-200 text-green-800 border border-green-300'}`}
                  >➕ Vytvořit nové pro vše</button>
                </div>
                {/* Souhrn aktuálního nastavení */}
                {(() => {
                  const counts = { skip: 0, replace: 0, merge: 0, create_new: 0 };
                  Object.values(duplicateDecisions).forEach(v => { if (counts[v] !== undefined) counts[v]++; });
                  return (
                    <div className={`mt-2 text-xs flex flex-wrap gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {counts.skip > 0 && <span>⏭️ Přeskočit: {counts.skip}</span>}
                      {counts.replace > 0 && <span>🔄 Nahradit: {counts.replace}</span>}
                      {counts.merge > 0 && <span>🔀 Sloučit: {counts.merge}</span>}
                      {counts.create_new > 0 && <span>➕ Nové: {counts.create_new}</span>}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Hromadné akce podle vlastníka */}
            {duplicates.length > 0 && ownerGroupsList.length > 1 && (
              <div className={`border rounded-lg p-3 mb-3 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <p className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Hromadné akce podle vlastníka:
                </p>
                <div className="space-y-2">
                  {ownerGroupsList.map(owner => {
                    const icos = ownerGroupsMap[owner];
                    const activeAction = ownerActions[owner] || null; // Explicitní stav, ne odvozený z ICO

                    const btnBase = 'text-xs px-2 py-1 rounded border transition font-medium flex items-center gap-1';
                    const activeStyles = {
                      skip:       isDarkMode ? 'bg-red-600 border-red-500 text-white ring-2 ring-red-400'           : 'bg-red-600 border-red-700 text-white ring-2 ring-red-400',
                      replace:    isDarkMode ? 'bg-orange-600 border-orange-500 text-white ring-2 ring-orange-400'   : 'bg-orange-500 border-orange-600 text-white ring-2 ring-orange-400',
                      merge:      isDarkMode ? 'bg-blue-600 border-blue-500 text-white ring-2 ring-blue-400'         : 'bg-blue-600 border-blue-700 text-white ring-2 ring-blue-400',
                      create_new: isDarkMode ? 'bg-green-600 border-green-500 text-white ring-2 ring-green-400'     : 'bg-green-600 border-green-700 text-white ring-2 ring-green-400',
                    };
                    const inactiveStyles = {
                      skip:       isDarkMode ? 'bg-red-900/30 border-red-700 text-red-300 hover:bg-red-800/50'           : 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100',
                      replace:    isDarkMode ? 'bg-orange-900/30 border-orange-700 text-orange-300 hover:bg-orange-800/50' : 'bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100',
                      merge:      isDarkMode ? 'bg-blue-900/30 border-blue-700 text-blue-300 hover:bg-blue-800/50'       : 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100',
                      create_new: isDarkMode ? 'bg-green-900/30 border-green-700 text-green-300 hover:bg-green-800/50'   : 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100',
                    };

                    return (
                      <div key={owner} className="flex flex-wrap items-center gap-2">
                        <span className={`text-sm min-w-[8rem] ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          <strong>{owner}</strong> ({icos.length}×)
                        </span>
                        {[
                          { action: 'skip',       label: '⏭️ Přeskočit' },
                          { action: 'replace',    label: '🔄 Nahradit' },
                          { action: 'merge',      label: '🔀 Sloučit' },
                          { action: 'create_new', label: '➕ Vytvořit nové' },
                        ].map(({ action, label }) => (
                          <button
                            key={action}
                            onClick={() => handleOwnerAction(owner, action)}
                            className={`${btnBase} ${activeAction === action ? activeStyles[action] : inactiveStyles[action]}`}
                          >
                            {activeAction === action && <span>✓</span>}
                            {label}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Individuální karty — jen do 100 duplicit, jinak jen shrnutí */}
            {duplicates.length > 100 ? (
              <div className={`border rounded-lg p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  📋 Nalezeno <strong>{duplicates.length}</strong> duplicit — příliš mnoho pro jednotlivé zobrazení. 
                  Použijte hromadné akce výše pro nastavení všech najednou.
                </p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Prvních 10 duplicit: {duplicates.slice(0, 10).map(d => d.ico).join(', ')}…
                </p>
              </div>
            ) : (
              duplicates.length > 0 && (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {duplicates.map((duplicate) => (
                    <div key={duplicate.ico} className={`border rounded-lg p-4 space-y-3 ${
                      isDarkMode 
                        ? 'border-gray-600 bg-gray-800' 
                        : 'border-gray-200 bg-white'
                    }`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className={`font-medium text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>IČO: {duplicate.ico}</h4>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Existující záznam */}
                        <div className={`border rounded p-3 ${
                          isDarkMode 
                            ? 'bg-red-900/20 border-red-600' 
                            : 'bg-red-50 border-red-200'
                        }`}>
                          <h5 className={`font-medium mb-2 ${
                            isDarkMode ? 'text-red-300' : 'text-red-800'
                          }`}>Existující záznam v databázi:</h5>
                          <div className={`text-sm space-y-1 ${
                            isDarkMode ? 'text-red-200' : 'text-red-700'
                          }`}>
                            <p><strong>Název:</strong> {duplicate.existingCompany.name}</p>
                            <p><strong>Adresa:</strong> {duplicate.existingCompany.address}</p>
                            <p><strong>Vlastník:</strong> {duplicate.existingCompany.owner || 'Není uveden'}</p>
                            <p><strong>Stav:</strong> {duplicate.existingCompany.status || 'Není uveden'}</p>
                            <p><strong>Aktivita:</strong> {duplicate.existingCompany.nextActionDate 
                              ? new Date(duplicate.existingCompany.nextActionDate).toLocaleDateString('cs-CZ')
                              : 'Není naplánována'}</p>
                          </div>
                        </div>

                        {/* Nová data k importu */}
                        <div className={`border rounded p-3 ${
                          isDarkMode 
                            ? 'bg-green-900/20 border-green-600' 
                            : 'bg-green-50 border-green-200'
                        }`}>
                          <h5 className={`font-medium mb-2 ${
                            isDarkMode ? 'text-green-300' : 'text-green-800'
                          }`}>Nová data k importu:</h5>
                          <div className={`text-sm space-y-1 ${
                            isDarkMode ? 'text-green-200' : 'text-green-700'
                          }`}>
                            <p><strong>Název:</strong> {duplicate.importData?.name}</p>
                            <p><strong>Adresa:</strong> {duplicate.importData?.address}</p>
                            <p><strong>Stav:</strong> {duplicate.importData?.status || 'Není uveden'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Výběr akce */}
                      <div className="space-y-2">
                        <label className={`block text-sm font-medium ${
                          isDarkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>Vyberte akci:</label>
                        <div className="space-y-2">
                          {[
                            { value: 'skip', label: '⏭️ Přeskočit import tohoto záznamu', color: isDarkMode ? 'text-red-300' : 'text-red-700' },
                            { value: 'replace', label: '🔄 Nahradit existující záznam novými daty', color: isDarkMode ? 'text-orange-300' : 'text-orange-700' },
                            { value: 'merge', label: '🔀 Sloučit data (doplnit kontakt a aktivitu, zachovat původní)', color: isDarkMode ? 'text-blue-300' : 'text-blue-700' },
                            { value: 'create_new', label: '➕ Vytvořit nový záznam (ignorovat duplicitu)', color: isDarkMode ? 'text-green-300' : 'text-green-700' },
                          ].map(({ value, label, color }) => (
                            <label key={value} className="flex items-center">
                              <input
                                type="radio"
                                name={`duplicate_${duplicate.ico}`}
                                value={value}
                                checked={duplicateDecisions[duplicate.ico] === value}
                                onChange={(e) => setDuplicateDecisions(prev => ({ ...prev, [duplicate.ico]: e.target.value }))}
                                className="mr-2"
                              />
                              <span className={color}>{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setStep(3)}
                className={`px-4 py-2 rounded transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                    : 'bg-gray-500 hover:bg-gray-600 text-white'
                }`}
              >
                Zpět
              </button>
              <button
                onClick={() => setStep(5)}
                className={`px-6 py-2 rounded transition-colors ${
                  isDarkMode 
                    ? 'bg-green-700 hover:bg-green-600 text-white' 
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                Pokračovat s importem
              </button>
            </div>
          </div>
        )}

        {/* Krok 5: Finální import */}
        {step === 5 && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Finální import</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setStep(duplicates.length > 0 ? 4 : 3)}
                  className={`px-3 py-1 text-sm rounded transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                >
                  Zpět
                </button>
              </div>
            </div>

            <div className={`border rounded-lg p-4 ${
              isDarkMode 
                ? 'bg-blue-900/20 border-blue-600 text-blue-100' 
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}>
              <p className="text-sm">
                Vše je připraveno k importu. Klikněte na tlačítko níže pro spuštění.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setStep(duplicates.length > 0 ? 4 : 3)}
                className={`px-4 py-2 rounded transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-600 hover:bg-gray-500 text-white' 
                    : 'bg-gray-500 hover:bg-gray-600 text-white'
                }`}
              >
                Zpět
              </button>
              <button
                onClick={handleImport}
                disabled={isLoading || !selectedUserId}
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
              >
                {isLoading ? `${updateMode ? 'Aktualizuji' : 'Importuji'}... ${Math.round(importProgress)}%` : updateMode ? 'Spustit aktualizaci' : 'Spustit import'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportWizard;
